import { IFormLayout } from '@/@types/models/model';
import { LayerModelSection } from '@/@types/services/mapService';

type GFIFieldLike = {
  source: string;
  sortorder?: number | null;
  model_section_id?: number | null;
};

const DEFAULT_COLUMNS = 1;

const getSortOrder = (value?: number | null) => (typeof value === 'number' ? value : Number.MAX_SAFE_INTEGER);

const sortFields = <T extends GFIFieldLike>(fields: T[]): T[] =>
  [...fields].sort((a, b) => {
    const sortDiff = getSortOrder(a.sortorder) - getSortOrder(b.sortorder);

    if (sortDiff !== 0) {
      return sortDiff;
    }

    return 0;
  });

const sortSections = (sections: LayerModelSection[]): LayerModelSection[] =>
  [...sections].sort((a, b) => {
    const sortDiff = getSortOrder(a.sortorder) - getSortOrder(b.sortorder);

    if (sortDiff !== 0) {
      return sortDiff;
    }

    return 0;
  });

const normalizeLayout = (layout: IFormLayout, fieldNames: string[]): IFormLayout | undefined => {
  const available = new Set(fieldNames);
  const used = new Set<string>();

  const normalized = layout
    .map((section) => {
      const names = section.fieldNames.filter((fieldName) => {
        if (!available.has(fieldName) || used.has(fieldName)) {
          return false;
        }

        used.add(fieldName);
        return true;
      });

      return {
        ...section,
        fieldNames: names,
      };
    })
    .filter((section) => section.fieldNames.length > 0);

  const remainingFieldNames = fieldNames.filter((fieldName) => !used.has(fieldName));

  if (remainingFieldNames.length > 0) {
    normalized.unshift({
      columns: DEFAULT_COLUMNS,
      fieldNames: remainingFieldNames,
    });
  }

  return normalized.length > 0 ? normalized : undefined;
};

const buildLayoutFromSections = <T extends GFIFieldLike>(
  fields: T[],
  sections?: LayerModelSection[] | null
): IFormLayout | undefined => {
  if (!sections || sections.length === 0) {
    return undefined;
  }

  const sortedFields = sortFields(fields);
  const sortedSections = sortSections(sections);
  const availableSectionIds = new Set(sortedSections.map((section) => section.id));
  const sectionFields = new Map<number, string[]>();
  const leadingFieldNames: string[] = [];

  sortedFields.forEach((field) => {
    if (field.model_section_id !== null && field.model_section_id !== undefined && availableSectionIds.has(field.model_section_id)) {
      const current = sectionFields.get(field.model_section_id) ?? [];
      current.push(field.source);
      sectionFields.set(field.model_section_id, current);
      return;
    }

    leadingFieldNames.push(field.source);
  });

  const layout: IFormLayout = [];

  if (leadingFieldNames.length > 0) {
    layout.push({
      columns: DEFAULT_COLUMNS,
      fieldNames: leadingFieldNames,
    });
  }

  sortedSections.forEach((section) => {
    const fieldNames = sectionFields.get(section.id) ?? [];

    if (fieldNames.length === 0) {
      return;
    }

    layout.push({
      title: section.title_token,
      columns: DEFAULT_COLUMNS,
      fieldNames,
    });
  });

  return layout.length > 0 ? layout : undefined;
};

export default <T extends GFIFieldLike>(
  fields: T[],
  sections?: LayerModelSection[] | null,
  layout?: IFormLayout
) => {
  const sortedFields = sortFields(fields);
  const sortedFieldNames = sortedFields.map((field) => field.source);
  const normalizedLayout = layout
    ? normalizeLayout(layout, sortedFieldNames)
    : buildLayoutFromSections(sortedFields, sections);

  return {
    fieldNames: normalizedLayout ? normalizedLayout.flatMap((section) => section.fieldNames) : sortedFieldNames,
    layout: normalizedLayout,
  };
};
